import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { PrivacyNotice, AgentDraftBadge } from "../components/Brand";
import { toast } from "sonner";
import { Sparkles, Mic, Type, Loader2, Send, CheckCircle2, ArrowLeft } from "lucide-react";

const SERVICE_TYPES = [
  "In-Classroom Coaching", "Co-Teaching", "Demonstration Lessons", "Lesson Planning",
  "Unit Planning", "Workshops", "Lesson Viewing and Feedback", "Study Groups/PLC",
  "Adapted Lesson Study", "Data Processing and Analysis", "Other",
];
const SUMMARY_FIELDS = [
  ["work_completed", "Work completed"],
  ["participants", "Participants / audience"],
  ["instructional_focus", "Instructional focus"],
  ["evidence_materials", "Evidence or materials used"],
  ["successes_observations", "Successes / observations"],
  ["follow_up_actions", "Follow-up actions"],
  ["internal_notes", "Internal notes (never shared externally)"],
];

export default function LogWork() {
  const { logId: routeLogId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [orgs, setOrgs] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [ctx, setCtx] = useState({ organization_id: "", school_id: "", engagement_id: "", date: new Date().toISOString().slice(0, 10), service_types: [], other_description: "" });
  const [schoolContacts, setSchoolContacts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [log, setLog] = useState(null);
  const [mode, setMode] = useState("text");
  const [note, setNote] = useState("");
  const [transcript, setTranscript] = useState("");
  const [transcriptConfirmed, setTranscriptConfirmed] = useState(false);
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [matched, setMatched] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/organizations"), api.get("/engagements")]).then(([o, e]) => {
      setOrgs(o.data);
      setEngagements(e.data);
    });
  }, []);

  useEffect(() => {
    if (routeLogId) return;
    const q = Object.fromEntries(searchParams.entries());
    if (q.organization_id || q.school_id || q.engagement_id) {
      setCtx((c) => ({ ...c,
        organization_id: q.organization_id || c.organization_id,
        school_id: q.school_id || c.school_id,
        engagement_id: q.engagement_id || c.engagement_id }));
    }
  }, [searchParams, routeLogId]);

  useEffect(() => {
    if (!routeLogId) return;
    api.get(`/logs/${routeLogId}`).then((r) => {
      const l = r.data;
      setLog(l);
      setCtx({ organization_id: l.organization_id, school_id: l.school_id, engagement_id: l.engagement_id, date: l.date, service_types: l.service_types || [], other_description: "" });
      setSessions(l.sessions || []);
      setNote(l.original_note || "");
      setTranscript(l.transcript || "");
      setTranscriptConfirmed(!!l.transcript_reviewed);
      if (l.input_mode === "voice") setMode("voice");
      if (l.agent_summary) { setSummary(l.agent_summary); setQuestions(l.agent_questions || []); setStep(3); }
      else if (l.original_note || l.transcript) setStep(2);
    }).catch(() => toast.error("Could not open that log."));
  }, [routeLogId]);

  const schools = orgs.find((o) => o.id === ctx.organization_id)?.schools || [];
  const orgEngagements = engagements.filter((e) => e.organization_id === ctx.organization_id);

  useEffect(() => {
    if (ctx.school_id) api.get(`/contacts?school_id=${ctx.school_id}&status=active`).then((r) => setSchoolContacts(r.data));
    else setSchoolContacts([]);
  }, [ctx.school_id]);

  const teacherContacts = schoolContacts.filter((c) => c.associations.some((a) => a.school_id === ctx.school_id && ["teacher", "instructional_coach"].includes(a.role)));

  const toggleServiceType = (t) => setCtx({
    ...ctx,
    service_types: ctx.service_types.includes(t) ? ctx.service_types.filter((x) => x !== t) : [...ctx.service_types, t],
  });

  const startLog = async () => {
    setBusy(true);
    try {
      const payload = { ...ctx, service_types: ctx.service_types.map((t) => t === "Other" && ctx.other_description ? `Other: ${ctx.other_description}` : t) };
      const { data } = await api.post("/logs", payload);
      setLog(data);
      setStep(2);
      window.history.replaceState(null, "", `/log-work/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const autosave = (patch) => {
    if (!log) return;
    setSaveState("Saving…");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/logs/${log.id}`, patch);
        setSaveState("Saved");
      } catch { setSaveState("Save failed"); }
    }, 1200);
  };

  const onNoteChange = (v) => { setNote(v); autosave({ original_note: v }); };

  const uploadAudio = async (file) => {
    if (!file || !log) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/logs/${log.id}/transcribe`, form);
      if (data.error) {
        toast.error(data.error);
      } else {
        setTranscript(data.transcript);
        setTranscriptConfirmed(false);
        toast.success("Transcribed. Please review names and math terminology below.");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const confirmTranscript = async () => {
    await api.patch(`/logs/${log.id}`, { transcript, transcript_reviewed: true, original_note: transcript });
    setTranscriptConfirmed(true);
    setNote(transcript);
    toast.success("Transcript confirmed as your note.");
  };

  const runAgent = async (withAnswers = false) => {
    const text = mode === "voice" ? transcript : note;
    if (!text.trim()) { toast.error("Add a note or transcript first."); return; }
    setBusy(true);
    try {
      const payload = withAnswers
        ? { answers: questions.map((q, i) => ({ ...q, answer: answers[i] || "" })) }
        : null;
      const { data } = withAnswers
        ? await api.post(`/logs/${log.id}/answers`, payload)
        : await api.post(`/logs/${log.id}/structure`, { note_text: text });
      setSummary(data.summary);
      setQuestions(data.questions || []);
      setAnswers({});
      setMatched(data.matched_contacts || []);
      setUnmatched(data.unmatched_participants || []);
      setStep(3);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveSummary = async () => {
    try {
      await api.patch(`/logs/${log.id}`, { agent_summary: summary, sessions });
      toast.success("Summary saved.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/logs/${log.id}`, { agent_summary: summary, sessions });
      await api.post(`/logs/${log.id}/submit`);
      toast.success("Daily record submitted for review.");
      navigate(`/logs/${log.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const steps = ["Context", "Your update", "Agent Draft", "Review & submit"];

  return (
    <div className="max-w-4xl space-y-6" data-testid="log-work-page">
      <div className="fade-up">
        <Link to="/" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-dashboard-link">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <p className="eyebrow">Operations Agent intake</p>
        <h1 className="section-title text-3xl sm:text-4xl mt-1">Log Today's Work</h1>
      </div>

      <ol className="flex flex-wrap gap-2 fade-up-1" data-testid="intake-steps">
        {steps.map((s, i) => (
          <li key={s} className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-colors ${
            step === i + 1 ? "bg-navy text-white border-navy" : step > i + 1 ? "bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]" : "bg-white text-[#78716C] border-[#E7E1D4]"
          }`} data-testid={`intake-step-${i + 1}`}>{i + 1} · {s}</li>
        ))}
      </ol>

      <PrivacyNotice />

      {step === 1 && (
        <div className="card p-6 sm:p-8 space-y-5 fade-up" data-testid="step-context-card">
          <h2 className="font-serif font-bold text-xl text-navy">Where did you work?</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label">Organization / District</label>
              <select className="input" value={ctx.organization_id} data-testid="select-organization"
                onChange={(e) => setCtx({ ...ctx, organization_id: e.target.value, school_id: "", engagement_id: "" })}>
                <option value="">Select…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">School</label>
              <select className="input" value={ctx.school_id} disabled={!ctx.organization_id} data-testid="select-school"
                onChange={(e) => setCtx({ ...ctx, school_id: e.target.value })}>
                <option value="">Select…</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Service engagement</label>
              <select className="input" value={ctx.engagement_id} disabled={!ctx.organization_id} data-testid="select-engagement"
                onChange={(e) => setCtx({ ...ctx, engagement_id: e.target.value })}>
                <option value="">Select…</option>
                {orgEngagements.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Date</label>
              <input type="date" className="input" value={ctx.date} data-testid="select-date"
                onChange={(e) => setCtx({ ...ctx, date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Services provided (select all that apply)</label>
              <div className="flex flex-wrap gap-2" data-testid="select-service-type">
                {SERVICE_TYPES.map((t) => (
                  <button key={t} type="button" data-testid={`service-type-${t.replace(/[^a-zA-Z]/g, "-")}`}
                    onClick={() => toggleServiceType(t)}
                    className={`rounded-full px-4 py-2 text-sm font-bold border transition-colors ${
                      ctx.service_types.includes(t) ? "bg-terracotta text-white border-terracotta" : "bg-white text-navy border-[#E7E1D4] hover:border-terracotta"
                    }`}>{t}</button>
                ))}
              </div>
              {ctx.service_types.includes("Other") && (
                <input className="input mt-3 max-w-md" placeholder="Describe the other service…" value={ctx.other_description}
                  data-testid="service-type-other-input"
                  onChange={(e) => setCtx({ ...ctx, other_description: e.target.value })} />
              )}
            </div>
          </div>
          <button className="btn-primary" disabled={busy || !ctx.organization_id || !ctx.school_id || !ctx.engagement_id || ctx.service_types.length === 0}
            onClick={startLog} data-testid="start-log-button">
            {busy ? "Starting…" : "Start daily record"}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="card p-6 sm:p-8 space-y-5 fade-up" data-testid="step-input-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-serif font-bold text-xl text-navy">Tell the Operations Agent about your day</h2>
            <div className="flex rounded-full border border-[#E7E1D4] overflow-hidden" data-testid="input-mode-toggle">
              <button className={`px-4 py-2 text-sm font-bold flex items-center gap-2 ${mode === "text" ? "bg-navy text-white" : "bg-white text-navy"}`}
                onClick={() => setMode("text")} data-testid="mode-text-button"><Type size={14} /> Type</button>
              <button className={`px-4 py-2 text-sm font-bold flex items-center gap-2 ${mode === "voice" ? "bg-navy text-white" : "bg-white text-navy"}`}
                onClick={() => setMode("voice")} data-testid="mode-voice-button"><Mic size={14} /> Voice note</button>
            </div>
          </div>

          {mode === "text" ? (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label mb-0">Written update</label>
                <span className="text-xs text-[#78716C]" data-testid="autosave-indicator">{saveState || "Autosaves as you type"}</span>
              </div>
              <textarea className="input min-h-[220px] leading-relaxed" value={note} onChange={(e) => onNoteChange(e.target.value)}
                placeholder="What did you work on? Who participated? What instructional focus, materials, observations, and next steps?"
                data-testid="note-textarea" />
            </div>
          ) : (
            <div className="space-y-4" data-testid="voice-panel">
              <label className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#E7E1D4] bg-canvas px-6 py-10 cursor-pointer hover:border-terracotta transition-colors"
                data-testid="audio-upload-dropzone">
                <Mic size={26} className="text-terracotta" />
                <span className="text-sm font-bold text-navy">{busy ? "Transcribing with Whisper…" : "Upload a voice note (mp3, m4a, wav, webm — max 25 MB)"}</span>
                <span className="text-xs text-[#78716C]">Recordings are stored privately. Admins control retention and deletion.</span>
                <input type="file" accept="audio/*,.m4a,.webm" className="hidden" disabled={busy}
                  onChange={(e) => uploadAudio(e.target.files?.[0])} data-testid="audio-file-input" />
              </label>
              {log?.transcription_error && (
                <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm text-[#991B1B]" data-testid="transcription-error">
                  {log.transcription_error} Your recording is preserved — retry above or type your note instead.
                </div>
              )}
              {transcript && (
                <div>
                  <label className="label">Review & correct the transcript (teacher names, math terminology)</label>
                  <textarea className="input min-h-[160px] leading-relaxed" value={transcript}
                    onChange={(e) => { setTranscript(e.target.value); setTranscriptConfirmed(false); }}
                    data-testid="transcript-textarea" />
                  <button className="btn-outline mt-3" onClick={confirmTranscript} disabled={transcriptConfirmed}
                    data-testid="confirm-transcript-button">
                    {transcriptConfirmed ? <><CheckCircle2 size={15} /> Transcript confirmed</> : "Confirm transcript"}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button className="btn-ghost" onClick={() => setStep(1)} data-testid="back-to-context-button">Back</button>
            <button className="btn-primary" onClick={() => runAgent(false)}
              disabled={busy || (mode === "voice" ? !transcript : !note.trim())} data-testid="run-agent-button">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {busy ? "Agent is reading…" : "Ask the Operations Agent"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && summary && (
        <div className="space-y-6 fade-up">
          <div className="agent-panel p-6 sm:p-8 space-y-5" data-testid="agent-summary-review-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-serif font-bold text-xl text-navy">Structured service record</h2>
              <AgentDraftBadge />
            </div>
            <p className="text-sm text-[#57534E]">
              The agent extracted only what your note states. Edit anything before submitting — your edits
              become the record of truth.
            </p>
            {questions.length > 0 && (
              <div className="rounded-lg border border-[#93C5FD] bg-[#EFF6FF] p-4 space-y-3" data-testid="agent-questions-panel">
                <div className="text-sm font-black text-[#1E40AF]">The agent needs a few essentials:</div>
                {questions.map((q, i) => (
                  <div key={i}>
                    <label className="label !text-[#1E40AF]">{q.question}</label>
                    <input className="input" value={answers[i] || ""} data-testid={`agent-answer-${i}`}
                      onChange={(e) => setAnswers({ ...answers, [i]: e.target.value })} />
                  </div>
                ))}
                <button className="btn-navy" onClick={() => runAgent(true)} disabled={busy} data-testid="submit-answers-button">
                  {busy ? "Updating…" : "Send answers to the agent"}
                </button>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              {SUMMARY_FIELDS.map(([key, label]) => (
                <div key={key} className={key === "work_completed" || key === "internal_notes" ? "sm:col-span-2" : ""}>
                  <label className={`label ${key === "internal_notes" ? "!text-[#991B1B]" : ""}`}>{label}</label>
                  <textarea className="input min-h-[80px]" value={summary[key] || ""} data-testid={`summary-field-${key}`}
                    onChange={(e) => setSummary({ ...summary, [key]: e.target.value })} />
                </div>
              ))}
            </div>
            {(matched.length > 0 || unmatched.length > 0) && (
              <div className="text-sm space-y-1.5" data-testid="participant-matching">
                {matched.length > 0 && (
                  <div><span className="font-bold text-[#065F46]">Matched to school directory:</span>{" "}
                    {matched.map((c) => `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email).join(", ")}</div>
                )}
                {unmatched.length > 0 && (
                  <div><span className="font-bold text-[#92400E]">Not in directory (no emails will be guessed):</span>{" "}
                    {unmatched.join(", ")}</div>
                )}
              </div>
            )}

            <div className="rounded-lg border border-[#E7E1D4] bg-white p-5 space-y-3" data-testid="sessions-builder">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-serif font-bold text-navy">Sessions & participating teachers</h3>
                  <p className="text-xs text-[#78716C]">Multiple sessions in one day still count as one billable day. Follow-ups go only to the teachers you select here — never the whole school.</p>
                </div>
                <button type="button" className="btn-outline !py-1.5 !px-3 text-xs" data-testid="add-session-button"
                  onClick={() => setSessions([...sessions, { id: `s${Date.now()}`, label: `Session ${sessions.length + 1}`, participant_ids: [] }])}>
                  + Add session</button>
              </div>
              {sessions.length === 0 && (
                <p className="text-xs text-[#78716C]" data-testid="sessions-empty">No sessions added — teacher follow-ups will default to the participants matched above.</p>
              )}
              {sessions.map((s, si) => (
                <div key={s.id} className="rounded-lg bg-canvas border border-[#E7E1D4] p-4 space-y-2.5" data-testid={`session-${si}`}>
                  <div className="flex items-center gap-2">
                    <input className="input !py-1.5 max-w-[240px]" value={s.label} data-testid={`session-label-${si}`}
                      onChange={(e) => setSessions(sessions.map((x, j) => (j === si ? { ...x, label: e.target.value } : x)))} />
                    <button type="button" className="btn-ghost !p-1.5 text-[#991B1B]" data-testid={`session-remove-${si}`}
                      onClick={() => setSessions(sessions.filter((_, j) => j !== si))}>×</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {teacherContacts.map((c) => {
                      const on = s.participant_ids.includes(c.id);
                      return (
                        <button key={c.id} type="button" data-testid={`session-${si}-participant-${c.id}`}
                          onClick={() => setSessions(sessions.map((x, j) => (j === si ? { ...x, participant_ids: on ? x.participant_ids.filter((p) => p !== c.id) : [...x.participant_ids, c.id] } : x)))}
                          className={`rounded-full px-3 py-1 text-xs font-bold border transition-colors ${
                            on ? "bg-terracotta text-white border-terracotta" : "bg-white text-navy border-[#E7E1D4] hover:border-terracotta"}`}>
                          {c.display_name || `${c.first_name} ${c.last_name}`}</button>
                      );
                    })}
                    {teacherContacts.length === 0 && <span className="text-xs text-[#78716C]">No active teachers/coaches in this school's directory.</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button className="btn-ghost" onClick={() => setStep(2)} data-testid="back-to-input-button">Back</button>
              <button className="btn-outline" onClick={saveSummary} data-testid="save-summary-button">Save edits</button>
              <button className="btn-primary" onClick={() => { saveSummary(); setStep(4); }} data-testid="continue-review-button">Continue to review</button>
            </div>
          </div>
        </div>
      )}

      {step === 4 && summary && (
        <div className="card p-6 sm:p-8 space-y-6 fade-up" data-testid="step-review-card">
          <h2 className="font-serif font-bold text-xl text-navy">Review & submit</h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid="review-context">
            <div><span className="font-bold text-[#78716C]">School:</span> {schools.find((s) => s.id === ctx.school_id)?.name || log?.school?.name}</div>
            <div><span className="font-bold text-[#78716C]">Date:</span> {ctx.date}</div>
            <div><span className="font-bold text-[#78716C]">Services:</span> {(ctx.service_types || []).join(" · ")}</div>
            <div><span className="font-bold text-[#78716C]">Source:</span> {mode === "voice" ? "Voice note (transcript reviewed)" : "Written note"}</div>
          </div>
          <div className="rounded-lg bg-canvas border border-[#E7E1D4] p-5 space-y-3">
            {SUMMARY_FIELDS.filter(([k]) => summary[k]).map(([k, label]) => (
              <div key={k} className="text-sm">
                <span className={`font-bold ${k === "internal_notes" ? "text-[#991B1B]" : "text-navy"}`}>{label}: </span>
                <span className="text-[#57534E]">{summary[k]}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#78716C]">
            Submitting sends this record to a CKingEducation Admin for review. Follow-up emails remain
            drafts until approved — and nothing sends automatically, even after approval.
          </p>
          <div className="flex items-center gap-3">
            <button className="btn-ghost" onClick={() => setStep(3)} data-testid="back-to-summary-button">Back</button>
            <button className="btn-primary" onClick={submit} disabled={busy} data-testid="submit-log-button">
              <Send size={15} /> {busy ? "Submitting…" : "Submit daily record"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
